import { Alert, Button, Input, Space, Table, Typography } from 'antd';
import { useFetchApiGet } from 'hooks/api/useFetchApi';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import nhsvSecuritiesPriceApiCall from 'apps/nhsv/apiCalls/nhsv/securities-price';
import { SearchOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface NHSVSecurityPrice {
  code: string;
  name: string;
  time: string;
  last: string;
  refPrice: string;
  avgPrice: string;
  ceiling: string;
  floor: string;
  open: string;
  high: string;
  low: string;
  change: string;
  changeRate: string;
  volume: string;
  foreignBuyVol: string;
  foreignSellVol: string;
  high52: string;
  low52: string;
}

const columns = [
  { title: 'Code', dataIndex: 'code', key: 'code', fixed: 'left' as const },
  { title: 'Name', dataIndex: 'name', key: 'name' },
  { title: 'Time', dataIndex: 'time', key: 'time' },
  { title: 'Last', dataIndex: 'last', key: 'last' },
  { title: 'Ref Price', dataIndex: 'refPrice', key: 'refPrice' },
  { title: 'Avg Price', dataIndex: 'avgPrice', key: 'avgPrice' },
  { title: 'Ceiling', dataIndex: 'ceiling', key: 'ceiling' },
  { title: 'Floor', dataIndex: 'floor', key: 'floor' },
  { title: 'Open', dataIndex: 'open', key: 'open' },
  { title: 'High', dataIndex: 'high', key: 'high' },
  { title: 'Low', dataIndex: 'low', key: 'low' },
  { title: 'Change', dataIndex: 'change', key: 'change' },
  { title: 'Change %', dataIndex: 'changeRate', key: 'changeRate' },
  { title: 'Volume', dataIndex: 'volume', key: 'volume' },
  { title: 'Foreign Buy', dataIndex: 'foreignBuyVol', key: 'foreignBuyVol' },
  { title: 'Foreign Sell', dataIndex: 'foreignSellVol', key: 'foreignSellVol' },
  { title: '52w High', dataIndex: 'high52', key: 'high52' },
  { title: '52w Low', dataIndex: 'low52', key: 'low52' },
];

export default function NHSVSecuritiesPricePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSymbols = searchParams.get('symbols') || '';
  const [inputValue, setInputValue] = useState(urlSymbols);
  const { data, load, isLoading, error } = useFetchApiGet<Record<string, NHSVSecurityPrice>>(
    nhsvSecuritiesPriceApiCall,
    { resourceName: 'data' }
  );

  const handleFetch = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    setSearchParams({ symbols: trimmed });
    load({ symbols: trimmed });
  };

  const rows = data
    ? Object.values(data).map((item) => ({ ...item, key: item.code }))
    : [];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>NHSV Securities Price (raw, direct from NHSV)</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Calls NHSV&apos;s <code>/market/securities-price</code> directly, bypassing our sync
        jobs and DB entirely — useful for checking whether a price discrepancy comes from
        NHSV&apos;s own data or from our storage/sync layer.
      </Text>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space>
          <Input
            placeholder="Comma-separated symbols, e.g. FPT,FUEVFVND (max 20)"
            size="large"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onPressEnter={handleFetch}
            style={{ width: 480 }}
            allowClear
          />
          <Button
            type="primary"
            size="large"
            icon={<SearchOutlined />}
            onClick={handleFetch}
            loading={isLoading}
          >
            Fetch from NHSV
          </Button>
        </Space>

        {error && (
          <Alert type="error" message="Failed to fetch from NHSV" description={String(error)} />
        )}

        {rows.length > 0 && (
          <Table
            columns={columns}
            dataSource={rows}
            loading={isLoading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            size="small"
          />
        )}
      </Space>
    </div>
  );
}
